import React from 'react';
import { useTranslation } from 'react-i18next';
import { InfoCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { VMConfig } from '../../../types/vm.type';
import InstanceTypeCard from '../components/InstanceTypeCard';
import { vmInstanceTypes } from '../../../constants/vmConstants';
import { KubiyComputeSizesTable } from '../../../components/shared';

interface Step2VMProps {
  config: VMConfig;
  setConfig: (config: VMConfig | ((prev: VMConfig) => VMConfig)) => void;
}

const Step2VM: React.FC<Step2VMProps> = ({ config, setConfig }) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          {t('virtualMachines.create.step1.instanceType')} *
        </label>
        <div className="grid grid-cols-1 gap-3">
          {vmInstanceTypes.map((instance) => (
            <InstanceTypeCard
              key={instance.id}
              instance={instance}
              selected={config.instanceType === instance.id}
              onClick={() => setConfig((prev) => ({ ...prev, instanceType: instance.id }))}
            />
          ))}
        </div>
        <div className="mt-4">
          <KubiyComputeSizesTable />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('virtualMachines.create.step1.storage')}
        </label>
        <input
          type="range"
          min="60"
          max="500"
          step="10"
          value={config.storage}
          onChange={(e) => setConfig((prev) => ({ ...prev, storage: parseInt(e.target.value) }))}
          className="w-full"
        />
        <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mt-1">
          <span>60 GB</span>
          <span className="font-bold">{config.storage} GB</span>
          <span>500 GB</span>
        </div>
      </div>

      <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={config.backups}
            onChange={(e) => setConfig((prev) => ({ ...prev, backups: e.target.checked }))}
            className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-black dark:text-white focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-700"
          />
          <div>
            <p className="font-medium text-gray-900 dark:text-white">{t('virtualMachines.create.step1.backups')}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Daily backups with 7-day retention (+$5/month)</p>
          </div>
        </label>

        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={config.monitoring}
            onChange={(e) => setConfig((prev) => ({ ...prev, monitoring: e.target.checked }))}
            className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-black dark:text-white focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-700"
          />
          <div>
            <p className="font-medium text-gray-900 dark:text-white">{t('virtualMachines.create.step1.monitoring')}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Real-time metrics and alerts (Free)</p>
          </div>
        </label>
      </div>

      <div className="pt-6 border-t border-gray-200 dark:border-gray-700 space-y-4">
        <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 dark:border-blue-500 p-4 rounded-r-lg">
          <div className="flex items-start gap-2">
            <InfoCircleOutlined className="text-blue-600 dark:text-blue-400 text-lg mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-1">
                {t('virtualMachines.create.step2.secureAccess')}
              </p>
              <p className="text-sm text-blue-800 dark:text-blue-200">
                {t('virtualMachines.create.step2.secureAccessDesc')}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-500 p-4 rounded-r-lg">
          <div className="flex items-start gap-2">
            <WarningOutlined className="text-yellow-600 dark:text-yellow-400 text-lg mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-yellow-900 dark:text-yellow-300 mb-1">
                {t('virtualMachines.create.step2.privateNetworksNotice') || 'Redes Privadas'}
              </p>
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                {t('virtualMachines.create.step2.privateNetworksNoticeDesc') ||
                  'Por el momento no tenemos redes privadas disponibles, pero estamos trabajando en ellas. Próximamente podrás crear máquinas virtuales en redes privadas.'}
              </p>
            </div>
          </div>
        </div>

        <label className="flex items-center gap-3 cursor-not-allowed opacity-75">
          <input
            type="checkbox"
            checked={config.needsPublicIP}
            disabled={true}
            className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-black dark:text-white focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-700 cursor-not-allowed"
          />
          <div>
            <p className="font-medium text-gray-900 dark:text-white">{t('virtualMachines.create.step2.needsPublicIP')}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">IP pública = exposición a Internet. Recomendamos SSM o ALB.</p>
          </div>
        </label>

        {config.needsPublicIP && (
          <div className="ml-8 space-y-4 bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('virtualMachines.create.step2.publicIPType')} *
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="publicIPType"
                    value="dynamic"
                    checked={config.publicIPType === 'dynamic'}
                    onChange={() => setConfig((prev) => ({ ...prev, publicIPType: 'dynamic' }))}
                    className="w-4 h-4 text-black dark:text-white focus:ring-black dark:focus:ring-white"
                  />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{t('virtualMachines.create.step2.dynamic')}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Cambia al reiniciar - barata/rápida, menos útil</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 cursor-not-allowed opacity-60">
                  <input
                    type="radio"
                    name="publicIPType"
                    value="elastic"
                    checked={config.publicIPType === 'elastic'}
                    onChange={() => {}}
                    disabled
                    className="w-4 h-4 text-black dark:text-white focus:ring-black dark:focus:ring-white"
                  />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{t('virtualMachines.create.step2.elastic')}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('virtualMachines.create.step2.elasticUnavailable')}</p>
                  </div>
                </label>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Step2VM;
